import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useTheme } from '../../../../middleware/ThemeContext';
import { useToast } from '../../../../components/toast/ToastContext';
import { FormActions, FormContainer, ModalContent, ModalOverlay } from '../../../../components/FormBuilder/CustomForm/FormFiller';
import FormTitle from '../../../../components/form/FormTitle';
import { FieldLabel } from '../../../../components/FormBuilder/FieldSettings/FieldSettingsModal';
import { SkeletonCell } from '../../../../components/SkeletonCell';
import CustomInput from '../../../../components/form/CustomInput';
import CustomTextArea from '../../../../components/form/CustomTextArea';
import AMSSelect from '../../../../components/AMSSelect';
import { Field, Scope } from '../../../../types/types';
import { Button } from '../../../../components/form/Button';
import CloseIcon from '../../../../assets/icons/CloseIcon';
import { LoadingSpinner } from '../../../ui/LoadingSpinner';
import DocumentIcon from '../../../../assets/icons/document-text';
import styled from 'styled-components';
import { CorporateTheme, CreateFormTheme, Theme } from '../../../../utils/themes';
import { hexToRgba } from '../../../../utils/helper';
import StyledText from '../../../../components/StyledComponents/StyledText';
import SaveIcon from '../../../../assets/icons/SaveIcon';
import NewPageIcon from '../../../../assets/icons/NewPageIcon';
import CrossIcon from '../../../../assets/icons/CrossIcon';
import { set } from 'lodash';
import { ActionMeta, MultiValue } from 'react-select';
import { useCreatePageMutation, useGetSinglePageQuery, useUpdatePageDetailsMutation } from '../../../../api/pageBuilderApiSlice';
import { EditablePage } from '../Pages';
import { useNavigate } from 'react-router-dom';

// ...existing code...

interface CreateWidgetModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    fieldId?: number | null; isSystemWidget?: boolean;
    editPageId: number | null
}


const toSlug = (value: string) =>
    value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');

const isValidExternalUrl = (value: string) => {
    try {
        const parsedUrl = new URL(value.trim());
        return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
        return false;
    }
};

type RoleOption = {
    value: string; // role id
    label: string; // role name
};

type TagOption = {
    value: string;
    label: string;
};

type PageType = 'internal' | 'external';

interface ComponentField {
    id: string;
    name: string;
    position: number;
    layoutType: 'row' | 'column';
    positionInRow: 'left' | 'right';
    fieldId?: string; // optional for separators
    type: 'field' | 'separator'; // NEW
    parentId: string | null;    // NEW: null = root level

}

const FIELDS_PAGE_SIZE = 20;

const NewPageModal: React.FC<CreateWidgetModalProps> = ({ isSystemWidget, isOpen, onClose, onSuccess, fieldId, editPageId }) => {
    const { theme } = useTheme();
    const showToast = useToast();
    const navigate = useNavigate();
    const [pageNameError, setPageNameError] = useState<string | null>(null);
    const [pageURLError, setPageURLError] = useState<string | null>(null);
    const [pageRoleError, setPageRoleError] = useState<string | null>(null);
    const [tags, setTags] = useState<string[]>([]);
    const [newTag, setNewTag] = useState<string>('');
    const [metaTags, setMetaTags] = useState<string[]>([]);
    const [newMetaTag, setNewMetaTag] = useState<string>('');
    const [isPageUrlManuallyEdited, setIsPageUrlManuallyEdited] = useState<boolean>(false);
    const [selectedRoles, setSelectedRoles] = useState<RoleOption[]>([]);
    const [localTagOptions, setLocalTagOptions] = useState<TagOption[]>([]);
    const [tagSearchTerm, setTagSearchTerm] = useState('');
    const [pageType, setPageType] = useState<PageType>('internal');
    const [openNewTab, setOpenNewTab] = useState<boolean>(false);
    const [pageVisibility, setPageVisibility] = useState<string | undefined>('public');
    const [createPage, { isLoading }] = useCreatePageMutation();
    const { data: getSinglePage, isLoading: pageDetailsLoading, isFetching: pageDetailsFetching } = useGetSinglePageQuery(editPageId!, { skip: !editPageId, refetchOnMountOrArgChange: true });
    const [updatePageDetails, { isLoading: updatePageLoading }] = useUpdatePageDetailsMutation();
    const tagCacheKey = useMemo(
        () => localTagOptions.map((option) => option.value.toLowerCase()).sort().join('|'),
        [localTagOptions]
    );

    const [{ name, description, url }, setStateFormDetails] = useState<{
        name: string;
        description: string;
        url: string;
    }>({
        name: '',
        description: '',
        url: '',
    });




    const resetForm = () => {
        setPageNameError(null);
        setPageRoleError(null);
        setPageURLError(null);
        setStateFormDetails({ name: '', description: '', url: '' });
        setTags([]);
        setMetaTags([]);
        setSelectedRoles([]);
        setPageType('internal');
        setOpenNewTab(false);
        setPageVisibility('public');
        setIsPageUrlManuallyEdited(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const resetFieldsOnPageTypeChange = () => {
        setPageNameError(null);
        setPageURLError(null);
        setPageRoleError(null);
        setStateFormDetails({ name: '', description: '', url: '' });
        setTags([]);
        setNewTag('');
        setMetaTags([]);
        setNewMetaTag('');
        setSelectedRoles([]);
        setOpenNewTab(false);
        setPageVisibility('public');
        setIsPageUrlManuallyEdited(false);
    };



    const validateForm = () => {
        let isValid = true;

        // Reset errors
        setPageNameError(null);
        setPageURLError(null);
        setPageRoleError(null);
        // Validate component name
        if (!name.trim()) {
            setPageNameError('Name is required');
            isValid = false;
        }

        if (!url.trim()) {
            setPageURLError('URL is required');
            isValid = false;
        } else if (pageType === 'external' && !isValidExternalUrl(url)) {
            setPageURLError('Please enter a valid external URL (http:// or https://).');
            isValid = false;
        }

        if ((pageType === 'internal' || pageType === 'external') && pageVisibility === 'private' && selectedRoles.length === 0) {
            setPageRoleError('Page role is required for private pages');
            isValid = false;
        }

        return isValid;
    };


    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            const payload = pageType === 'external'
                ? {
                    pageType,
                    visibility: pageVisibility,
                    name,
                    url,
                    openNewTab,
                    tags: tags,
                    roleIds: pageVisibility === 'private'
                        ? selectedRoles.map((role) => Number(role.value))
                        : [],
                }
                : {
                    pageType,
                    visibility: pageVisibility,
                    tags: tags,
                    name,
                    url,
                    roleIds: pageVisibility === 'private'
                        ? selectedRoles.map((role) => Number(role.value))
                        : [],
                    seoSettings: {
                        metaDescription: description,
                        metaTags: metaTags
                    }
                };


            let call = editPageId ? updatePageDetails({ id: editPageId, data: payload }) : createPage(payload);
            let response = await call.unwrap();

            let message = editPageId ? "updated" : "created";
            showToast(`New page ${message}`, `New Page has been ${message}`, "success");
            if (!editPageId && pageType !== 'external') {
                navigate(`/home/page-builder/${response?.page?.id}`);
            }
            handleClose();
            onSuccess();


        } catch (err: any) {
            console.error("Form creation error:", err?.data?.message);

            const message =
                err?.data?.message ||
                "Failed to create page. Please try again.";

            showToast("Page Creation Failed", message, "error");
        }
    };




    const mapFieldsResponse = useCallback((json: any, pageNumber: number) => {
        // roles array fetch karenge instead of fields
        const roles = json?.roles ?? [];
        const perPage = json?.perPage ?? json?.pagination?.perPage ?? 20;
        const totalCount = json?.totalCount ?? json?.pagination?.total ?? 0;
        const hasMore = totalCount ? pageNumber * perPage < totalCount : roles.length === perPage;

        // roles ko dropdown-friendly format me map karenge
        const options = roles.map((role: any) => ({
            label: role.name,     // dropdown me name show hoga
            value: String(role.id), // dropdown me id select hoga
            ...role,               // agar baaki data chahiye to
        }));

        return {
            options,
            hasMore,
            additional: { page: pageNumber + 1 },
        };
    }, []);

    const mapTagsResponse = useCallback((json: any, pageNumber: number) => {
        const apiTags = Array.isArray(json?.tags) ? json.tags : [];
        const meta = json?.meta ?? {};
        const currentPage = Number(meta?.page ?? pageNumber);
        const perPage = meta?.perPage ?? FIELDS_PAGE_SIZE;
        const total = meta?.total ?? apiTags.length;
        const totalPages = meta?.totalPages ?? (perPage > 0 ? Math.ceil(total / perPage) : 1);
        const hasMore = currentPage < totalPages;

        const normalizedApiOptions: TagOption[] = apiTags
            .map((tag: unknown) => String(tag ?? '').trim())
            .filter(Boolean)
            .map((tag: string) => ({ label: tag, value: tag }));

        const normalizedSearch = tagSearchTerm.trim().toLowerCase();
        const filteredLocalOptions = localTagOptions.filter((option) => {
            if (!normalizedSearch) return true;
            return option.label.toLowerCase().includes(normalizedSearch);
        });

        const mergedMap = new Map<string, TagOption>();
        [...normalizedApiOptions, ...filteredLocalOptions].forEach((option) => {
            mergedMap.set(option.value.toLowerCase(), option);
        });

        return {
            options: Array.from(mergedMap.values()),
            hasMore,
            additional: { page: currentPage + 1 },
        };
    }, [localTagOptions, tagSearchTerm]);


    const handleChange = (selected: MultiValue<RoleOption>) => {
        setSelectedRoles(Array.from(selected)); // selected roles store karna
        setPageRoleError(null);
    };

    const handleTagsChange = (selected: MultiValue<TagOption>) => {
        setTags(Array.from(selected).map((item) => item.value));
    };

    const handleCreateTag = (term: string) => {
        const normalized = term.trim();
        if (!normalized) return;

        const alreadySelected = tags.some((tag) => tag.toLowerCase() === normalized.toLowerCase());
        const alreadyInLocal = localTagOptions.some((option) => option.value.toLowerCase() === normalized.toLowerCase());

        if (!alreadyInLocal) {
            setLocalTagOptions((prev) => [...prev, { label: normalized, value: normalized }]);
        }

        if (!alreadySelected) {
            setTags((prev) => [...prev, normalized]);
        }
    };


    useEffect(() => {
        if (!editPageId) {
            resetForm();
            return;
        }

        // 🟡 EDIT MODE but data not loaded yet
        if (!getSinglePage?.page) return;
        setStateFormDetails({
            name: getSinglePage?.page.name ?? '',
            description: getSinglePage?.page?.seoSettings?.metaDescription ?? '',
            url: getSinglePage?.page.url ?? '',
        });

        setPageVisibility(getSinglePage?.page.visibility ?? 'public');
        const incomingPageType = getSinglePage?.page?.pageType ?? getSinglePage?.page?.type;
        setPageType(incomingPageType === 'external' ? 'external' : 'internal');
        setOpenNewTab(Boolean(getSinglePage?.page?.openNewTab ?? getSinglePage?.page?.openInNewTab));
        setTags(getSinglePage?.page.tags ? getSinglePage?.page.tags.split(',') : []);
        setMetaTags(getSinglePage?.page.seoSettings?.metaTags ?? []);
        setSelectedRoles(
            getSinglePage?.page.roles?.map((role: any) => ({
                label: role.name,
                value: String(role.id),
                ...role
            })) ?? []
        );
        setIsPageUrlManuallyEdited(false);

    }, [getSinglePage?.page, editPageId]);

    return (
        <Modal show={isOpen} onHide={handleClose} className='border-0'>
            <ModalOverlay>
                <ModalContent theme={theme}>
                    <div className='my-0'>
                        <FormTitle title={editPageId ? "Edit Page" : "Add New Page"} />
                    </div>
                    <FormContainer theme={theme}>
                        <BlueFormContainer theme={theme}>
                            <div className="px-3 py-2">
                                <div className="mb-4" style={{ padding: "40px", backgroundColor: theme.CorporateTheme.colors.background, borderRadius: '4px', border: '1px dashed #BABDC1' }}>
                                    <StyledText className="mb-2 text-center" fontSize="16px" fontWeight={600}>
                                        Start Creating New Custom Page
                                    </StyledText>
                                    <StyledText className="text-center mb-4" textColor={theme.CorporateTheme.colors.textSecondary} fontSize="14px" fontWeight={500}>
                                        You can select the page visibility & page name & you are good to go
                                    </StyledText>

                                    <FieldLabel theme={theme}>
                                        Page Type <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                                    </FieldLabel>

                                    {pageDetailsLoading || pageDetailsFetching ?
                                        <SkeletonCell height="40px" width='100%' />
                                        : <AMSSelect
                                            showIcons
                                            staticOptions={[{ value: 'internal', label: 'Internal Page' }, { value: 'external', label: 'External Page' }]}
                                            placeholder="Page Type"
                                            value={
                                                pageType
                                                    ? {
                                                        value: pageType,
                                                        label: pageType === 'internal' ? 'Internal Page' : 'External Page'
                                                    }
                                                    : undefined
                                            }
                                            onChange={(option) => {
                                                const nextPageType =
                                                    option?.value !== undefined && option?.value !== null
                                                        ? String(option.value)
                                                        : undefined;

                                                if (nextPageType === 'internal' || nextPageType === 'external') {
                                                    if (nextPageType !== pageType) {
                                                        resetFieldsOnPageTypeChange();
                                                    }
                                                    setPageType(nextPageType as PageType);
                                                }
                                            }}
                                            isDisabled={Boolean(editPageId)}
                                            defaultOptions
                                        />}

                                    <FieldLabel theme={theme} className="mt-4">
                                        Page Visibility <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                                    </FieldLabel>

                                    {pageDetailsLoading || pageDetailsFetching ?
                                        <SkeletonCell height="40px" width='100%' />
                                        : <AMSSelect
                                            showIcons
                                            staticOptions={[{ value: 'public', label: 'Public' }, { value: 'private', label: 'Private' }, { value: 'members-only', label: 'Members Only' }]}
                                            placeholder="Page Visibility"
                                            value={
                                                pageVisibility
                                                    ? {
                                                        value: pageVisibility,
                                                        label: pageVisibility === 'private' ? 'Private' : pageVisibility === 'members-only' ? 'Members Only' : 'Public'
                                                    }
                                                    : undefined
                                            }
                                            onChange={(option) => {
                                                const nextVisibility =
                                                    option?.value !== undefined && option?.value !== null
                                                        ? String(option.value)
                                                        : undefined;

                                                setPageVisibility(
                                                    nextVisibility
                                                );

                                                if (nextVisibility !== 'private') {
                                                    setSelectedRoles([]);
                                                    setPageRoleError(null);
                                                }
                                            }}

                                            defaultOptions
                                        />}
                                </div>
                                <FieldLabel theme={theme}>
                                    Page Name <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                                </FieldLabel>
                                {pageDetailsLoading || pageDetailsFetching ?
                                    <SkeletonCell height="40px" className='mt-2' />
                                    : <CustomInput
                                        type="text"
                                        placeholder="Enter page name"
                                        value={name}
                                        onChange={(value: string) => {
                                            const generatedUrl = toSlug(value);
                                            const shouldAutoGenerateUrl = pageType === 'internal' && !isPageUrlManuallyEdited;
                                            setStateFormDetails((prev) => ({
                                                ...prev,
                                                name: value,
                                                url: shouldAutoGenerateUrl ? generatedUrl : prev.url
                                            }));
                                            setPageNameError(null);
                                            if (shouldAutoGenerateUrl && generatedUrl.trim()) {
                                                setPageURLError(null);
                                            }
                                        }}
                                        disabled={isSystemWidget}
                                        error={pageNameError ?? undefined}
                                        required
                                    />}
                            </div>

                            <div className="px-3 py-2">

                                <FieldLabel theme={theme}>
                                    Page URL <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                                </FieldLabel>
                                {pageDetailsLoading || pageDetailsFetching ?
                                    <SkeletonCell height="40px" className='mt-2' />
                                    : <CustomInput
                                        type="text"
                                        placeholder={pageType === 'external' ? 'Enter external URL (https://example.com)' : 'Enter page url here'}
                                        value={url}
                                        onChange={(value: string) => {
                                            setIsPageUrlManuallyEdited(true);
                                            setStateFormDetails((prev) => ({ ...prev, url: value }));
                                            setPageURLError(null);
                                        }}
                                        disabled={isSystemWidget}
                                        error={pageURLError ?? undefined}
                                        required
                                    />}
                            </div>



                            {(pageType === 'internal' || pageType === 'external') && pageVisibility === 'private' && (
                                <div className="px-3 py-2">

                                    <FieldLabel theme={theme}>
                                        Page Role <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                                    </FieldLabel>
                                    {pageDetailsLoading || pageDetailsFetching ?
                                        <SkeletonCell height="40px" className='mt-2' />
                                        :
                                        <AMSSelect
                                            isMulti
                                            showDataType
                                            // showIcons
                                            isSearchable
                                            includeAccessKey
                                            endpoint="/contact-roles"
                                            placeholder={'Select page roles'}
                                            limit={FIELDS_PAGE_SIZE}
                                            value={selectedRoles}
                                            onChange={handleChange}
                                            mapResponse={mapFieldsResponse}
                                            // defaultOptions={!fieldSelectDisabled}
                                            getOptionValue={(option: any) => String(option?.id ?? option?.value ?? '')}
                                            error={pageRoleError ?? undefined}
                                        />
                                    }
                                </div>
                            )}

                            

                            {(pageType === 'internal' || pageType === 'external') && <div className="px-3 py-2">

                                <FieldLabel theme={theme}>
                                    Page Tags
                                </FieldLabel>
                                {pageDetailsLoading || pageDetailsFetching ?
                                    <SkeletonCell height="40px" className='mt-2' />
                                    :
                                    // <CustomInput
                                    //     type="text"
                                    //     placeholder="Enter tags here"
                                    //     value={newTag}
                                    //     onChange={(value: string) => setNewTag(value)}

                                    //     onKeyDown={(e) => {
                                    //         if (e.key === 'Enter' && newTag.trim()) {
                                    //             e.preventDefault();
                                    //             if (!tags.includes(newTag.trim())) {
                                    //                 setTags([...tags, newTag.trim()]);
                                    //             }
                                    //             setNewTag('');
                                    //         }
                                    //     }}
                                    //     disabled={isSystemWidget}
                                    // // error={pageNameError ?? undefined}
                                    // // required
                                    // />

                                    <AMSSelect
                                        isMulti
                                        isSearchable
                                        endpoint="/builder/websites/page-tags"
                                        placeholder={'Select page tags'}
                                        limit={1000}
                                        maxMenuHeight={180}
                                        value={tags.map((tag: any) => ({ label: tag, value: tag }))}
                                        onChange={handleTagsChange}
                                        mapResponse={mapTagsResponse}
                                        cacheUniqs={[tagCacheKey]}
                                        includeAddButton
                                        addButtonText='Create Tag'
                                        onAddButtonClick={handleCreateTag}
                                        onInputChange={(value) => {
                                            setTagSearchTerm(value);
                                            return value;
                                        }}
                                        onMenuClose={() => setTagSearchTerm('')}
                                        // shouldLoadMore={(scrollHeight, clientHeight, scrollTop) => {
                                        //     if (scrollHeight <= clientHeight) {
                                        //         return false;
                                        //     }
                                        //     return scrollHeight - clientHeight - scrollTop < 8;
                                        // }}

                                        getOptionValue={(option: any) => String(option?.value ?? '')}
                                    />
                                }
                                {/* Render selected tags as chips */}
                                {/* <TagListContainer >
                                    {pageDetailsLoading || pageDetailsFetching ?

                                        Array.from({ length: 3 }).map((_, index) => (
                                            <SkeletonCell key={index} height="40px" width='90px' className='rounded-pill' />
                                        ))
                                        :

                                        <>
                                            {tags.map((tag: string, index: number) => (
                                                <TagChip
                                                    key={index}
                                                    style={{ gap: '10px', backgroundColor: theme.CorporateTheme.colors.background, border: `1px solid ${CreateFormTheme.colors.inputBorders}`, padding: '8px 15px' }}
                                                >
                                                    <StyledText fontWeight={500} fontSize='12px' textColor={theme.CorporateTheme.colors.graniteGrey}>{tag}</StyledText>

                                                    <div className='d-flex align-items-center justify-content-center' onClick={() => setTags(tags.filter((_, i) => i !== index))}>
                                                        <CrossIcon size={18} color={theme.CorporateTheme.colors.graniteGrey} />
                                                    </div>
                                                </TagChip>
                                            ))}
                                        </>}
                                </TagListContainer> */}
                            </div>}

                            {pageType === 'external' && (
                                <div className="px-3 py-2 mt-3">
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={openNewTab}
                                            onChange={(e) => setOpenNewTab(e.target.checked)}
                                            disabled={isSystemWidget}
                                        />
                                        <StyledText fontSize='14px' fontWeight={500} textColor={theme.CorporateTheme.colors.headerColor}>
                                            Want to open in new tab?
                                        </StyledText>
                                    </label>
                                </div>
                            )}

                        </BlueFormContainer>

                        {pageType === 'internal' && <div className='d-flex flex-column' style={{ padding: "20px 35px" }}>
                            <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Page SEO Settings</StyledText>
                            <div className='mt-4'>

                                <FieldLabel theme={theme}>
                                    Meta Tags
                                </FieldLabel>
                                {pageDetailsLoading || pageDetailsFetching ?
                                    <SkeletonCell height="40px" className='mt-2' />
                                    : <CustomInput
                                        type="text"
                                        placeholder="Enter tags here"
                                        value={newMetaTag}
                                        onChange={(value: string) => setNewMetaTag(value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && newMetaTag.trim()) {
                                                e.preventDefault();
                                                if (!metaTags.includes(newMetaTag.trim())) {
                                                    setMetaTags([...metaTags, newMetaTag.trim()]);
                                                }
                                                setNewMetaTag('');
                                            }
                                        }}
                                        disabled={isSystemWidget}

                                    />}
                                <TagListContainer >
                                    {pageDetailsLoading || pageDetailsFetching ?

                                        Array.from({ length: 3 }).map((_, index) => (
                                            <SkeletonCell key={index} height="40px" width='90px' className='rounded-pill' />
                                        ))
                                        :
                                        <>
                                            {metaTags.map((tag, index) => (

                                                <TagChip
                                                    key={index}
                                                    style={{ gap: '10px', backgroundColor: theme.CorporateTheme.colors.background, border: `1px solid ${CreateFormTheme.colors.inputBorders}`, padding: '8px 15px' }}
                                                >
                                                    <StyledText fontWeight={500} fontSize='12px' textColor={theme.CorporateTheme.colors.graniteGrey}>{tag}</StyledText>

                                                    <div className='d-flex align-items-center justify-content-center' onClick={() => setMetaTags(metaTags.filter((_, i) => i !== index))}>
                                                        <CrossIcon size={18} color={theme.CorporateTheme.colors.graniteGrey} />
                                                    </div>
                                                </TagChip>
                                            ))}</>}
                                </TagListContainer>
                            </div>
                            <div >
                                <FieldLabel theme={theme}>Meta Description</FieldLabel>
                                {pageDetailsLoading || pageDetailsFetching ? <SkeletonCell height="100px" className='mt-2' /> : <CustomTextArea
                                    type="textarea"
                                    placeholder="Enter description here"
                                    value={description}
                                    disabled={isSystemWidget}
                                    onChange={(value: string) =>
                                        setStateFormDetails((prev) => ({ ...prev, description: value }))
                                    }
                                />}
                            </div>
                        </div>}


                    </FormContainer>

                    {/* Actions */}
                    <FormActions style={{ gap: '5px', marginTop: '15px' }}>
                        <Button theme={theme} type="button" $variant="cancel" onClick={handleClose} disabled={isLoading || updatePageLoading}>
                            <CloseIcon color={theme.CorporateTheme.colors.headerColor} /> Close
                        </Button>
                        <Button theme={theme} type="submit"
                            disabled={isLoading || updatePageLoading}
                            $variant="customButton" onClick={handleSubmit} btnBg={theme?.CorporateTheme?.colors?.primary}
                            color={theme?.CorporateTheme?.colors?.background} borderColor='none'>
                            {isLoading || updatePageLoading ? <LoadingSpinner size={'20px'} color={theme?.CorporateTheme?.colors?.background} /> : <NewPageIcon size={20} color={theme?.CorporateTheme?.colors?.background} />}
                            {editPageId ? "Update Page" : "Create This Page"}
                        </Button>
                    </FormActions>
                </ModalContent>
            </ModalOverlay>
        </Modal>
    );
};

const BlueFormContainer = styled.div<{ theme: Theme }>`
  background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.blueberry, 0.05)};
  margin: 15px;
  border : 1px solid ${({ theme }) => theme.CorporateTheme.colors.blueberry};
  border-left: 3px solid ${({ theme }) => theme.CorporateTheme.colors.blueberry};
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  padding: 10px 0px;
`;

export const TagListContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem; /* equivalent to gap-2 */
  margin-top: 20px; /* equivalent to mt-2 */
`;

export const TagChip = styled.span<{ theme?: any }>`
  border-radius: 9999px; 
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default NewPageModal;